import { Check, EditOutlined, Loop, RemoveCircleOutline, Replay, SmartToyOutlined } from '@mui/icons-material'
import dayjs from 'dayjs'
import { memo, type ReactNode } from 'react'
import { MicroLearningHistoryEntry, MicroLearningLogAction } from '../../../../api/models'
import styles from './MicroLearningHistoryCard.module.css'

type ActionConfig = {
  icon: ReactNode
  label: string
  feedbackLabel?: string
  description?: string
}

const ACTION_CONFIG: Record<MicroLearningLogAction, ActionConfig> = {
  generated: {
    icon: <SmartToyOutlined className={styles.icon} />,
    label: 'Generated',
  },
  approved: {
    icon: <Check className={styles.icon} />,
    label: 'Approved',
    feedbackLabel: 'FEEDBACK OPTIONAL',
    description: 'Micro-Learning approved',
  },
  regenerate_success: {
    icon: (
      <Loop
        className={styles.icon}
        style={{ transform: 'scaleX(-1)' }}
      />
    ),
    label: 'Regenerated Successfully',
    description: 'Micro-Learning regenerated',
  },
  regenerate_fail: {
    icon: (
      <Loop
        className={styles.icon}
        style={{ transform: 'scaleX(-1)' }}
      />
    ),
    label: 'Failed to Regenerate',
    description: 'Please, try again',
  },
  dismissed: {
    icon: <span className={styles.dismissedIcon}>-</span>,
    label: 'Dismissed',
    description: 'Insufficient evidence to support the learning.',
    feedbackLabel: 'FEEDBACK OPTIONAL',
  },
  refine_regen_requested: {
    icon: <Replay className={styles.icon} />,
    label: 'Refine & Regenerate Requested',
    feedbackLabel: 'FEEDBACK',
  },
  edited: {
    icon: <EditOutlined className={styles.icon} />,
    label: 'Edited',
  },
}

type MicroLearningHistoryCardProps = {
  entry: MicroLearningHistoryEntry
  isFirst?: boolean
}

const formatDate = (dateStr: string): string => {
  const d = dayjs(dateStr)
  return d.isValid() ? d.format('MMM D, YYYY, h:mm A') : dateStr
}

const formatEditedFields = (fields: string[]): string => {
  if (fields.length === 1) return `${fields[0]} was edited`
  return `${fields.slice(0, -1).join(', ')} and ${fields[fields.length - 1]} were edited`
}

const buildDescription = (entry: MicroLearningHistoryEntry): string | null => {
  if (entry.note) {
    return entry.note
  }

  if (entry.action === 'edited' && entry.changed_fields?.length) {
    return formatEditedFields(entry.changed_fields)
  }

  const config = ACTION_CONFIG[entry.action]

  return config.description ?? null
}

const MicroLearningHistoryCard = ({ entry, isFirst = false }: MicroLearningHistoryCardProps) => {
  const config = ACTION_CONFIG[entry.action]
  const hasFeedback = !!entry.feedback && !!config.feedbackLabel
  const hasExperimentChanges =
    entry.experiment_changes &&
    (entry.experiment_changes.reclustered.length > 0 || entry.experiment_changes.excluded.length > 0)
  const description = buildDescription(entry)

  return (
    <div
      className={styles.card}
      data-action={entry.action}>
      <div className={styles.iconColumn}>
        <span className={styles.iconCircle}>{config.icon}</span>
      </div>

      <div className={styles.content}>
        <div className={styles.header}>
          <span className={styles.actionTitle}>{config.label}</span>
          {isFirst && <span className={styles.currentBadge}>Current</span>}
          {hasFeedback && <span className={styles.feedbackBadge}>Feedback selected</span>}
        </div>

        <span className={styles.actorLine}>
          {entry.actor.name} &middot; {formatDate(entry.created_at)}
        </span>

        {((description && !hasFeedback) || entry.note) && <p className={styles.descriptionText}>{description}</p>}

        {hasFeedback && (
          <div className={styles.feedbackBanner}>
            <p className={styles.feedbackLabel}>{config.feedbackLabel}</p>
            <p className={styles.feedbackText}>{entry.feedback}</p>
          </div>
        )}

        {hasExperimentChanges && (
          <div className={styles.experimentChanges}>
            {entry.experiment_changes!.reclustered.length > 0 && (
              <div className={styles.experimentLine}>
                <Replay className={styles.experimentLineIcon} />
                <span className={styles.experimentLabel}>
                  {entry.experiment_changes!.reclustered.length} experiment
                  {entry.experiment_changes!.reclustered.length > 1 ? 's' : ''} re-clustered
                </span>
                <span className={styles.experimentCodes}>{entry.experiment_changes!.reclustered.join(', ')}</span>
              </div>
            )}
            {entry.experiment_changes!.excluded.length > 0 && (
              <div className={styles.experimentLine}>
                <RemoveCircleOutline className={styles.experimentLineIcon} />
                <span className={styles.experimentLabel}>
                  {entry.experiment_changes!.excluded.length} experiment
                  {entry.experiment_changes!.excluded.length > 1 ? 's' : ''} excluded
                </span>
                <span className={styles.experimentCodes}>{entry.experiment_changes!.excluded.join(', ')}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default memo(MicroLearningHistoryCard)
